import { renderSeed } from './missions.js';
import { extractGitState } from './git/graph.js';
import { applyTemplate, gradeMission, makeContext } from './grader/index.js';
import { runCheck } from './grader/checks.js';
import { revealSpoiler } from './spoilers.js';
import { hiddenSeedValues, withholdHidden } from './mentor-hidden.js';
/**
 * The spoiler rung a situation's five authored prose fields are hidden under.
 *
 * Rungs 1 to 5 are the hint ladder, 0 is the reflection and 6 is the debrief
 * (spoilers.ts, scripts/build-missions.mjs), so 7 is the next free number.
 * scripts/build-missions.mjs hides them under the same constant, written out
 * there because that script does not import this package.
 */
const SITUATION_RUNG = 7;
export function summarize(s) {
    return {
        id: s.id,
        missionId: s.mission.id,
        driver: s.workspace.driver,
        createdAt: s.createdAt,
        hintLevel: s.hintLevel,
        checks: s.checks,
        completedAt: s.completedAt,
        commandCount: s.telemetry.filter((e) => e.type === 'command').length,
    };
}
/**
 * Whether the last Check still describes the workspace.
 *
 * True only when there is one, it was taken with evidence, and nothing that
 * can move the workspace has happened since it BEGAN: no command finished at
 * the prompt, no editor save, no reset. Those three are the only ways this
 * tab sees the tree change, so this is as current as the tab can know — and
 * when in doubt it answers false, which costs a re-grade rather than a wrong
 * answer.
 *
 * From the start of the grade and not its end (`startedAt`, not `gradedAt`),
 * because the objectives are checked one after another over seconds, and a
 * save that lands between the first and the last leaves a grade that is half
 * the old file and half the new one. Stamped at the end, that grade looked
 * current, and was sent as the learner's own Check. For the same reason an
 * event in the same millisecond as the start counts as after it: the clock
 * cannot say which came first, so it is taken as the one that makes the grade
 * old. A grade with no start recorded is one this cannot vouch for.
 */
export function checkIsCurrent(s) {
    const last = s.lastGrade;
    if (!last || last.evidence !== true || last.startedAt === undefined)
        return false;
    const since = last.startedAt;
    return !s.telemetry.some((e) => (e.type === 'command' || e.type === 'save' || e.type === 'reset') && e.t >= since);
}
/** The tail of each workspace's queue of grading work (`inTurn`). */
const turns = new WeakMap();
/**
 * Run `task` once everything already queued on this workspace has finished,
 * and never beside it.
 *
 * Grading reads a live workspace through one guest, and two gradings at once
 * interleave there: a Check and the mentor's look (`peekGrade`, the situation
 * recognizers, the evidence script) are sequences of commands, not single
 * ones, and levels' checks share fixed paths and ports — l19 adds and removes
 * git worktrees, the HTTP levels start the learner's server on its one port.
 * A Check pressed while a question is being answered is the ordinary case
 * that makes them meet, since the button stays live.
 *
 * So each workspace has one queue. Nothing is dropped or merged: every task
 * runs, in the order it was asked for. A task that fails fails only its own
 * caller; the queue goes on with the next. Keyed on the workspace rather than
 * the session because the workspace is what is shared — a reset replaces it,
 * and the new tree owes nothing to work still draining from the old one — and
 * because it lets `gatherMentorEvidence`, which is handed a workspace and not
 * a session, take its turn in the same queue.
 *
 * Not re-entrant: a task that asks for a turn on its own workspace waits for
 * itself. Nothing queued here calls anything else that is.
 */
export function inTurn(ws, task) {
    const run = (turns.get(ws) ?? Promise.resolve()).then(task);
    turns.set(ws, run.then(() => undefined, () => undefined));
    return run;
}
/**
 * The workspace as a look the learner did not ask for should use it: every
 * command run with `GIT_OPTIONAL_LOCKS=0`.
 *
 * `git status` — `working_tree_clean`, and any script that asks — refreshes
 * the index as it goes, and refreshing takes `index.lock`; a learner who
 * types `git add` at that moment is told another git process is running, by
 * a process they never started. The variable turns that refresh off and
 * nothing else: the answers are the same, and a lock git needs to change
 * anything is still taken. It does not reach a `git diff` in a `shell` check,
 * which refreshes under the lock whatever it says (git 2.46); the evidence
 * script, whose own `git diff` is the one that runs on every question, reads
 * a copy of the index instead (mentor-evidence.ts). The learner's own Check
 * is left as it was.
 */
function withoutOptionalLocks(ws) {
    const quiet = Object.create(ws);
    quiet.exec = (cmd, opts = {}) => ws.exec(cmd, { ...opts, env: { ...opts.env, GIT_OPTIONAL_LOCKS: '0' } });
    return quiet;
}
/**
 * The grade as the mentor may see it: a copy whose check output has every
 * value this learner has to find and has not been shown blanked
 * (`withholdHidden`, mentor-hidden.ts, which says why). A copy, because the
 * grade handed in may be `lastGrade`, which is what the learner's own screen
 * draws from.
 */
function withheld(grade, s) {
    if (!grade.objectives.some((o) => o.output))
        return grade;
    const hidden = hiddenSeedValues(s.mission, s.seed);
    if (hidden.length === 0)
        return grade;
    return {
        ...grade,
        objectives: grade.objectives.map((o) => (o.output ? { ...o, output: withholdHidden(o.output, hidden) } : o)),
    };
}
/** Turn seed variables into env for author scripts: LOST_COMMIT -> SEED_LOST_COMMIT. */
export function seedEnv(seed) {
    const env = {};
    for (const [k, v] of Object.entries(seed))
        env[`SEED_${k}`] = v;
    return env;
}
/**
 * Session ids are short, random, and only ever used to address a session.
 *
 * `globalThis.crypto` is the one random source a browser and Node 20+ agree
 * on, and the reason no crypto module from Node is imported here.
 */
function newSessionId() {
    const source = globalThis.crypto;
    if (!source)
        throw new Error('no Web Crypto available: EngineerOS needs Node 20+ or a current browser');
    const bytes = source.getRandomValues(new Uint8Array(6));
    return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}
export class SessionManager {
    driver;
    missions;
    seeder;
    sessions = new Map();
    constructor(driver, missions, seeder) {
        this.driver = driver;
        this.missions = missions;
        this.seeder = seeder;
    }
    list() {
        return [...this.sessions.values()].map(summarize);
    }
    get(id) {
        const s = this.sessions.get(id);
        if (s)
            s.lastActive = Date.now();
        return s;
    }
    async create(missionId) {
        const mission = this.missions.get(missionId);
        if (!mission)
            throw new Error(`unknown mission "${missionId}"`);
        const id = newSessionId();
        const workspace = await this.driver.create(id);
        let seed;
        try {
            seed = await this.seeder.seed(workspace, mission);
        }
        catch (err) {
            await workspace.destroy();
            throw err;
        }
        const now = Date.now();
        const session = {
            id,
            mission,
            workspace,
            seed,
            createdAt: now,
            lastActive: now,
            hintLevel: 0,
            checks: 0,
            completedAt: null,
            lastGrade: null,
            telemetry: [{ t: now, type: 'created', data: { missionId } }],
            shell: null,
        };
        this.sessions.set(id, session);
        return session;
    }
    /** Back to the mission checkpoint: fresh workspace, same session and telemetry. */
    async reset(id) {
        const s = this.sessions.get(id);
        if (!s)
            throw new Error(`unknown session "${id}"`);
        s.shell?.kill();
        s.shell = null;
        await s.workspace.destroy();
        s.workspace = await this.driver.create(id);
        s.seed = await this.seeder.seed(s.workspace, s.mission);
        s.lastGrade = null;
        // Forgotten only now the new tree is seeded, and not on the way in: a
        // state read that lands mid-reset sees the wiped or half-extracted tree,
        // and must not be the one that records where this attempt starts.
        s.startHead = undefined;
        s.lastActive = Date.now();
        s.telemetry.push({ t: Date.now(), type: 'reset' });
        return s;
    }
    async destroy(id) {
        const s = this.sessions.get(id);
        if (!s)
            return;
        this.sessions.delete(id);
        s.shell?.kill();
        await s.workspace.destroy();
    }
    async destroyAll() {
        await Promise.all([...this.sessions.keys()].map((id) => this.destroy(id)));
    }
    /** Destroy sessions idle for longer than `maxIdleMs`. */
    async sweep(maxIdleMs) {
        const cutoff = Date.now() - maxIdleMs;
        const stale = [...this.sessions.values()].filter((s) => s.lastActive < cutoff);
        await Promise.all(stale.map((s) => this.destroy(s.id)));
        return stale.length;
    }
    /**
     * The repository as it stands. The first read after a create or a reset
     * also records where the level started (`startHead`), which costs nothing:
     * both runtimes read the state straight after either, to draw the graph —
     * `BrowserRuntime.start` and `api.reset` in the tab, the session and reset
     * routes on the dev server. Nothing else knows a new session's id before
     * that read, and a read racing it after a reset (the terminal's, when the
     * respawned shell draws its prompt) reads the same fresh tree. Recording it
     * any other way would be one more round trip into the guest on every level
     * open.
     *
     * Only a read that began with nothing recorded may record, so a read that
     * started before a reset finished cannot land after it and write down a
     * tree the reset was still replacing.
     */
    async state(s) {
        const unrecorded = s.startHead === undefined;
        const state = await extractGitState(s.workspace, s.mission.cwd);
        if (unrecorded && s.startHead === undefined)
            s.startHead = state.head.hash;
        return state;
    }
    /**
     * The learner pressed Check.
     *
     * Taken with evidence, so that the grade this leaves in `lastGrade` already
     * carries what each failing check printed and a question put to the mentor
     * straight afterwards can be answered from it rather than by grading again
     * (`gradeForMentor`). The outputs go nowhere but to the learner's own
     * screen and the mentor: the progress record keeps only the flags, the
     * funnel counts objectives, and verification grades on a server without
     * the option. The dev server's routes hand the grade to their own page,
     * which is the same learner.
     *
     * In turn with every other grading of this workspace (`inTurn`), and the
     * session is updated inside the turn, so a question queued behind a Check
     * finds that Check already in `lastGrade`.
     */
    grade(s) {
        const { workspace, seed } = s;
        return inTurn(workspace, async () => {
            const result = await gradeMission(s.mission, workspace, seed, { evidence: true });
            s.checks += 1;
            s.lastGrade = result;
            s.lastActive = Date.now();
            if (result.passed && s.completedAt === null)
                s.completedAt = Date.now();
            s.telemetry.push({
                t: Date.now(),
                type: 'check',
                data: { passed: result.passed, failed: result.objectives.filter((o) => !o.passed).map((o) => o.id) },
            });
            return result;
        });
    }
    /**
     * Grade the workspace as it stands, for the mentor, and change nothing.
     *
     * Not a Check, and it must never be able to act like one. It does not count
     * (`checks` is the stuck detector's and the plausibility floor's), it does
     * not replace `lastGrade` (the objectives on screen are the ones the learner
     * last asked for), it does not complete the level even when everything
     * passes (a level is cleared by pressing Check, with the plausibility flags
     * the runtime attaches there), and it writes no telemetry and no
     * `lastActive`. Asking for help is not work on the level.
     *
     * It waits its turn behind any Check (`inTurn`), and it takes no optional
     * git lock (`withoutOptionalLocks`): it runs while the learner is typing.
     */
    peekGrade(s) {
        const { workspace, seed } = s;
        return inTurn(workspace, () => this.look(s, workspace, seed));
    }
    /** `peekGrade`'s grade, for callers already holding the workspace's turn. */
    look(s, workspace, seed) {
        return gradeMission(s.mission, withoutOptionalLocks(workspace), seed, { evidence: true });
    }
    /**
     * The grade a question to the mentor should be answered against, and whose
     * it is.
     *
     * The learner's own last Check when it still describes the workspace
     * (`checkIsCurrent`): it costs nothing, and its messages are ones they have
     * already read. Otherwise a fresh `peekGrade`, because a grade taken before
     * the learner's last command is a description of a workspace that no longer
     * exists — and the prompt calls it what is true now. That is the bug this
     * replaced: `lastGrade` was sent however old it was, so a learner who had
     * fixed the file and not pressed Check was told about the version they had
     * already fixed.
     *
     * Decided inside the workspace's turn, so a question asked while a Check
     * is running waits for it and is then answered from it rather than
     * grading the same tree a second time.
     */
    gradeForMentor(s) {
        const { workspace, seed } = s;
        return inTurn(workspace, async () => {
            const last = s.lastGrade;
            if (last && checkIsCurrent(s))
                return { grade: withheld(last, s), source: 'check' };
            return { grade: withheld(await this.look(s, workspace, seed), s), source: 'mentor' };
        });
    }
    /**
     * The first situation whose recognizer matches this workspace, in author
     * order, or null.
     *
     * A situation is a named wrong state this level was authored to recognize
     * (missions.ts, `Situation`), and the id this answers with is what the tab
     * puts into `MentorContext.situation` — so it is read by the model, which is
     * told what the state is called and what the author says about it in the
     * level bundle. It names nothing to the learner on its own.
     *
     * It runs the grader's own `runCheck` against the grader's own context, and
     * that is deliberate to the point of being the rule: a recognizer is a check
     * spec with the same grammar as a diagnostic, and a second evaluator for it
     * would be a second set of bugs and a second definition of what `shell`
     * means. `applyTemplate` is the strict renderer, as it is for an objective,
     * so a recognizer naming a seed value this workspace does not have throws
     * rather than quietly comparing against the literal `{{seed.KEY}}`.
     *
     * A recognizer that throws is not a match and is not fatal. One broken
     * situation must not be able to take help off the air for the level, and the
     * failure it produces — no situation in the context — is exactly the state of
     * a level that has no situations at all, which is most of the course.
     *
     * Public, and called by the tab in two places: once to build the context a
     * question is asked with, and once by the stuck detector, which changes what
     * it offers when the app can already see which named wrong state somebody is
     * in. It is not folded into `grade` for the reason it is not called on every
     * check: matching runs the recognizers in author order until one hits, which
     * on a level with seven of them is up to seven more round trips into the
     * emulator on top of the objectives the check already ran. That is a real
     * cost on a machine this slow, and it buys nothing for the learner who is
     * checking because they are making progress. The caller decides.
     *
     * The recognizers are checks run against the live tree, so they take the
     * workspace's turn like any grading (`inTurn`), and like a look for the
     * mentor they take no optional git lock (`withoutOptionalLocks`).
     */
    async matchSituation(s) {
        const situations = s.mission.situations ?? [];
        if (situations.length === 0)
            return null;
        const { workspace, seed } = s;
        return inTurn(workspace, async () => {
            const ctx = makeContext(withoutOptionalLocks(workspace), s.mission.cwd);
            for (const sit of situations) {
                try {
                    const outcome = await runCheck(ctx, applyTemplate({ check: sit.check, with: sit.with }, seed));
                    if (outcome.ok)
                        return sit.id;
                }
                catch {
                    // A recognizer this build cannot run is a recognizer that did not match.
                }
            }
            return null;
        });
    }
    /**
     * One situation with its five prose fields readable, or null.
     *
     * The sibling of `revealedHints`, and hidden the same way for a different
     * audience: a hint is withheld because it is paid for, and a situation's
     * words are withheld because reading them in missions.json would hand a
     * curious learner the shape of every mistake the level expects, which is
     * most of the level. Rung 7 is theirs (1-5 hints, 0 the reflection, 6 the
     * debrief), and a mission that was never built — the dev server, the author
     * tests — passes through reveal unchanged.
     *
     * Revealed, then rendered, in that order, for the reason `revealedHints`
     * gives: the seed reference is inside the hidden text.
     */
    revealedSituation(s, id) {
        const sit = (s.mission.situations ?? []).find((x) => x.id === id);
        if (!sit)
            return null;
        const show = (text) => revealSpoiler(text, s.mission.id, SITUATION_RUNG);
        return renderSeed({
            ...sit,
            say: show(sit.say),
            ...(sit.ask ? { ask: show(sit.ask) } : {}),
            ...(sit.look ? { look: show(sit.look) } : {}),
            ...(sit.choices ? { choices: sit.choices.map((c) => ({ ...c, text: show(c.text), reply: show(c.reply) })) } : {}),
        }, s.seed);
    }
    /** Reveal the next rung of the hint ladder. Returns every hint revealed so far. */
    hint(s) {
        if (s.hintLevel < s.mission.hints.length) {
            s.hintLevel += 1;
            s.telemetry.push({ t: Date.now(), type: 'hint', data: { level: s.hintLevel } });
        }
        return { level: s.hintLevel, hints: this.revealedHints(s) };
    }
    /**
     * The hints the learner has paid for, readable. A built missions.json
     * hides every rung (spoilers.ts); the dev server and the author tests
     * carry plain text, on which reveal is the identity. Either way the
     * mission object keeps whatever it was given and only the copy handed
     * out is readable, so a rung not yet asked for stays hidden in memory too.
     */
    revealedHints(s) {
        const { id } = s.mission;
        // Revealed, then rendered, and in that order: the seed reference is
        // inside the hidden text, so there is nothing to render until the rung
        // has been unhidden. A rung that names the value the level turns on —
        // "the four spans belong to {{seed.REQ}}" — is a rung the learner must
        // read as a sentence and not as a placeholder, and `s.seed` is the whole
        // truth about this workspace: the values it was seeded with, and anything
        // `boot.sh` printed when it opened.
        return s.mission.hints
            .slice(0, s.hintLevel)
            .map((h) => renderSeed({ ...h, text: revealSpoiler(h.text, id, h.level) }, s.seed));
    }
    /** The reflection, readable, once the mission has passed; null before. It is hidden as rung 0. */
    reflection(s) {
        if (s.completedAt === null)
            return null;
        return renderSeed(revealSpoiler(s.mission.reflection, s.mission.id, 0), s.seed);
    }
    recordCommand(s, command) {
        s.lastActive = Date.now();
        s.telemetry.push({ t: Date.now(), type: 'command', data: command });
    }
    /** An editor write. Counted as work alongside commands, because some levels never touch the prompt. */
    recordSave(s, path) {
        s.lastActive = Date.now();
        s.telemetry.push({ t: Date.now(), type: 'save', data: path });
    }
}
//# sourceMappingURL=session.js.map